import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Quote, Link as LinkIcon, Image as ImageIcon, Undo, Redo, Minus } from 'lucide-react';
import { blogApi } from '../../lib/adminApi';

const btnCls = (active) =>
  `inline-flex items-center justify-center h-8 w-8 rounded-md text-[13px] transition-colors ${
    active ? 'bg-brand-blue text-white' : 'text-navy hover:bg-slate-100'
  }`;

const RichEditor = ({ value, onChange }) => {
  const fileInputRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-brand-blue font-semibold hover:underline' } }),
      Image.configure({ HTMLAttributes: { class: 'rounded-lg my-4' } }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  // Keep editor in sync when parent replaces value (e.g. loading a post to edit)
  useEffect(() => {
    if (!editor) return;
    if (value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const addLink = () => {
    const prev = editor.getAttributes('link').href || '';
    // eslint-disable-next-line no-alert
    const url = window.prompt('URL', prev);
    if (url === null) return;
    if (url === '') editor.chain().focus().extendMarkRange('link').unsetLink().run();
    else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const uploadAndInsertImage = async (file) => {
    try {
      const { url } = await blogApi.upload(file);
      editor.chain().focus().setImage({ src: url, alt: '' }).run();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert('Upload failed. Check that you are logged in.');
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="rich-editor">
      <div className="flex flex-wrap items-center gap-1 border-b border-slate-100 px-2 py-2 bg-slate-50">
        <button type="button" aria-label="Bold" className={btnCls(editor.isActive('bold'))} onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-4 w-4" /></button>
        <button type="button" aria-label="Italic" className={btnCls(editor.isActive('italic'))} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-4 w-4" /></button>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <button type="button" aria-label="H2" className={btnCls(editor.isActive('heading', { level: 2 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></button>
        <button type="button" aria-label="H3" className={btnCls(editor.isActive('heading', { level: 3 }))} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></button>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <button type="button" aria-label="Bulleted list" className={btnCls(editor.isActive('bulletList'))} onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="h-4 w-4" /></button>
        <button type="button" aria-label="Numbered list" className={btnCls(editor.isActive('orderedList'))} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></button>
        <button type="button" aria-label="Quote" className={btnCls(editor.isActive('blockquote'))} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="h-4 w-4" /></button>
        <button type="button" aria-label="Divider" className={btnCls(false)} onClick={() => editor.chain().focus().setHorizontalRule().run()}><Minus className="h-4 w-4" /></button>
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <button type="button" aria-label="Link" className={btnCls(editor.isActive('link'))} onClick={addLink}><LinkIcon className="h-4 w-4" /></button>
        <button type="button" aria-label="Insert image" data-testid="editor-image-btn" className={btnCls(false)} onClick={() => fileInputRef.current?.click()}><ImageIcon className="h-4 w-4" /></button>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
               onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAndInsertImage(f); e.target.value = ''; }} />
        <span className="ml-auto flex items-center gap-1">
          <button type="button" aria-label="Undo" className={btnCls(false)} onClick={() => editor.chain().focus().undo().run()}><Undo className="h-4 w-4" /></button>
          <button type="button" aria-label="Redo" className={btnCls(false)} onClick={() => editor.chain().focus().redo().run()}><Redo className="h-4 w-4" /></button>
        </span>
      </div>
      <EditorContent editor={editor} className="prose-post p-5 min-h-[360px] focus-within:outline-none [&_.ProseMirror]:min-h-[300px] [&_.ProseMirror]:outline-none" />
    </div>
  );
};

export default RichEditor;
